/* Copyright (c) 2017 Backpack Health LLC */

import React, { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import './FrequencyInput.styles.scss';

import { formatFrequency } from '../../../../models/HealthEntryTextFormatting';

import { Card, CardRowUI } from '../../../../bphComponents/bundle';
import COLORS from '../../../styles/Colors';
import { useFrequencyList } from '../../../hooks/useMedli';

const originalFrequencyGroupChoices = [
  { label: <FormattedMessage id="list.frequency.hours.name" values={{ digit1: 2 }} />, value: 'hours', isActive: false },
  { label: <FormattedMessage id="list.frequency.days.name" values={{ digit1: 2 }} />, value: 'days', isActive: false },
  { label: <FormattedMessage id="list.frequency.weeks.name" values={{ digit1: 2 }} />, value: 'weeks', isActive: false },
  { label: <FormattedMessage id="list.frequency.months.name" values={{ digit1: 2 }} />, value: 'months', isActive: false },
  { label: <FormattedMessage id="list.frequency.other.name" values={{ digit1: 2 }} />, value: 'other', isActive: false },
];

interface Props {
  frequencyId: number;
  value: string;
  selectedFrequency: any;
  goToFrequencyUnits(g): void;
  onChangeFrequency(d): void;
}

const FrequencyInput: React.FC<Props> = (props) => {
  const { selectedFrequency, frequencyId, value, onChangeFrequency, goToFrequencyUnits } = props;

  const [state, setState] = React.useState({
    selectedGroups: [],
    isFreqExpanded: !frequencyId,
  });

  const [, freqActions] = useFrequencyList();
  const frequencyGroups = freqActions.getGroupedFrequencies();

  const selectedId = frequencyId;
  const frequencyGroupChoices = originalFrequencyGroupChoices.map((f) => ({ ...f, isActive: state.selectedGroups.includes(f.value) }));

  const getChoices = (item) => {
    const choices = [...(frequencyGroups[item.value] || []).map((a) => a)];
    const selectedId = frequencyId;
    const selectedIndex = choices.findIndex(f => f.id === selectedId && f.value === value);
    if (selectedIndex > -1) {
      const selectedChoice = choices.splice(selectedIndex, 1)[0];
      choices.unshift(selectedChoice);
    }
    const selectedFrequencyIsForGroup = selectedFrequency.id && item.value === selectedFrequency.group;
    if (selectedFrequencyIsForGroup && choices.filter((f) => f.key === selectedFrequency.key && f.value === value).length === 0) {
      choices.unshift({
        id: selectedFrequency.id,
        value,
      });
    }
    return choices;
  };

  const toggleSelectedGroup = (item) => {
    const newGroups = [...state.selectedGroups];
    const foundIndex = newGroups.indexOf(item.value);
    if (foundIndex === -1) {
      newGroups.push(item.value);
    } else {
      newGroups.splice(foundIndex, 1);
    }
    return newGroups;
  };

  return (
    <Card.CardShape>
      <Card.CollapsibleSummaryWithControl
        label={(
          <div className="font-caption-1 font-color-gsMedDark">
            <FormattedMessage id="amtFreq.frequency.field.label" />
          </div>
        )}
        disableCollapseUntilValue={true}
        hasBottomBorder={true}
        value={selectedId ? <div className="font-color-profile-accent">{formatFrequency({ id: selectedId, value })}</div> : null}
        isExpanded={state.isFreqExpanded}
        isMultiLine={false}
        onClick={() => {
          const startGroup = frequencyGroupChoices.find(g => {
            return !!getChoices(g).find(c => c.id === selectedId);
          });
          setState({
            isFreqExpanded: !state.isFreqExpanded,
            selectedGroups: toggleSelectedGroup(startGroup),
          });
        }}
      >

      {
        frequencyGroupChoices.map((item, key) => {
          const choices = getChoices(item);
          const isExpanded = state.selectedGroups.includes(item.value);
          const hasMore = item.value !== 'other';

          return (
            <Fragment key={key}>
              <Card.CardContent
                backgroundColor={COLORS.gsLightest}
                hasBottomBorder={true}
                onClick={() => {
                  setState({
                    isFreqExpanded: state.isFreqExpanded,
                    selectedGroups: toggleSelectedGroup(item),
                  });
                }}
              >
                <Card.CollapsibleControl
                  isMultiLine={false}
                  isExpanded={isExpanded}
                >
                  <span style={{ textTransform: 'capitalize' }}>
                    {item.label}
                  </span>
                </Card.CollapsibleControl>
              </Card.CardContent>

              <Card.CollapsibleContent isExpanded={isExpanded}>
                {
                  choices.map((f, i) => {
                    const isSelected = f.id === selectedId && f.value === value;
                    return (
                      <CardRowUI.SelectableRow
                        key={`${key}${i}`}
                        onClick={() => {
                          if (f.id === selectedId && f.value === value) {
                            return;
                          }

                          onChangeFrequency({ ...f });

                          setState({ selectedGroups: [], isFreqExpanded: false });
                        }}
                        selectedClassName="fill-color-profile"
                        isSelected={isSelected}
                        hasBottomBorder={true}
                      >
                        {formatFrequency({ id: f.id, value: f.value })}
                      </CardRowUI.SelectableRow>
                    );
                  })
                }
                {
                  hasMore && (
                    <CardRowUI.TextAction hasBottomBorder={true} forceBottomBorder={true} onClick={() => { goToFrequencyUnits(item.value); }}>
                      <div className="font-body font-color-profile-accent">
                        <FormattedMessage id="amtFreq.unit.search.label" />
                      </div>
                    </CardRowUI.TextAction>
                  )
                }
              </Card.CollapsibleContent>
            </Fragment>
          );
        })
      }
      </Card.CollapsibleSummaryWithControl>
    </Card.CardShape>
  );
};

export default FrequencyInput;
